import React from 'react';
import { Box, ButtonBase, CircularProgress, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddRounded as AddIcon,
  CloseRounded as CloseIcon,
  DescriptionOutlined as DocumentIcon,
  EmailOutlined as EmailIcon,
  ImageOutlined as ImageIcon,
  MicNoneRounded as MicIcon,
  PhotoCameraOutlined as CameraIcon,
  SellOutlined as TagIcon,
  SendRounded as SendIcon,
} from '@mui/icons-material';
import colors from '../../../assets/theme/colors';

const sourceChoices = [
  { label: 'Camera', ariaLabel: 'Take photo for log update', Icon: CameraIcon, handler: 'onTakePhoto' },
  { label: 'Photo', ariaLabel: 'Upload image for log update', Icon: ImageIcon, handler: 'onUploadImage' },
  { label: 'Document', ariaLabel: 'Upload file for log update', Icon: DocumentIcon, handler: 'onUploadFile' },
  { label: 'Forward email', ariaLabel: 'Forward an email', Icon: EmailIcon, handler: 'onForwardEmail' },
];

export const CaptureSourceChoices = ({ open = false, composerSaving = false, ...handlers }) => {
  if (!open) return null;

  const visibleChoices = sourceChoices.filter(({ handler }) => typeof handlers[handler] === 'function');
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(visibleChoices.length, 4)}, minmax(0, 1fr))`, gap: 0.75, mb: 1 }}>
      {visibleChoices.map(({ label, ariaLabel, Icon, handler }) => (
        <ButtonBase
          key={label}
          disabled={composerSaving}
          onClick={handlers[handler]}
          aria-label={ariaLabel}
          sx={{ minHeight: 72, px: 0.5, py: 0.8, display: 'flex', flexDirection: 'column', gap: 0.45, borderRadius: '16px', bgcolor: colors.semantic.sageMist, color: colors.semantic.profileFlow.completionText }}
        >
          <Icon sx={{ fontSize: 25, color: 'inherit' }} />
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 650, color: colors.semantic.ink, lineHeight: 1.1 }}>
            {label}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  );
};

export const CaptureTagSuggestions = ({ open = false, chips = [], handlers = {}, child, onDismiss, composerSaving = false }) => {
  if (!open || !chips.length) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.6, overflowX: 'auto', mb: 0.9, pb: 0.25, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      {chips.map((chip) => {
        const palette = chip.logType?.palette || {};
        const IconComponent = chip.logType?.iconComponent;
        const ink = palette.text || colors.semantic.sageDeep;
        return (
          <ButtonBase
            key={chip.id}
            disabled={composerSaving}
            onClick={(event) => {
              event?.stopPropagation?.();
              onDismiss?.();
              handlers[chip.handler]?.(child);
            }}
            aria-label={`Log ${chip.label}`}
            sx={{ minHeight: 38, flexShrink: 0, px: 1, py: 0.45, borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: 0.45, bgcolor: palette.softBg || alpha(ink, 0.1), color: ink, border: `1px solid ${alpha(ink, 0.08)}` }}
          >
            {IconComponent ? <IconComponent sx={{ fontSize: 18, color: 'inherit' }} /> : null}
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 750, color: 'inherit', lineHeight: 1 }}>{chip.label}</Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const roundActionSx = {
  width: 48,
  height: 48,
  flexShrink: 0,
  bgcolor: colors.semantic.sageMist,
  color: colors.semantic.ink,
  '&:hover': { bgcolor: colors.semantic.sageMist },
};

export const CaptureComposerActionRow = ({
  sourcesOpen,
  onToggleSources,
  tagsOpen,
  onToggleTags,
  showTags,
  onRecordVoice,
  onContinue,
  hasComposerContent,
  composerSaving,
  composerSavingLabel,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.85 }}>
    <IconButton onClick={onToggleSources} disabled={composerSaving} aria-label={sourcesOpen ? 'Hide capture choices' : 'Add to capture'} aria-expanded={sourcesOpen} sx={roundActionSx}>
      {sourcesOpen ? <CloseIcon /> : <AddIcon />}
    </IconButton>
    {showTags ? (
      <ButtonBase onClick={onToggleTags} disabled={composerSaving} aria-label="Choose a log type" aria-expanded={tagsOpen} sx={{ minHeight: 44, px: 1.2, gap: 0.55, borderRadius: '999px', bgcolor: tagsOpen ? alpha(colors.semantic.profileFlow.completionText, 0.18) : colors.semantic.sageMist, color: colors.semantic.ink }}>
        <TagIcon sx={{ fontSize: 21 }} />
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 650 }}>Tag</Typography>
      </ButtonBase>
    ) : null}
    <Box sx={{ flex: 1 }} />
    <IconButton onClick={onRecordVoice} disabled={composerSaving} aria-label="Record or upload a voice note" sx={{ color: colors.semantic.profileFlow.completionText }}>
      <MicIcon />
    </IconButton>
    <IconButton
      onClick={onContinue}
      disabled={!hasComposerContent || composerSaving}
      aria-label={composerSaving ? composerSavingLabel : 'Send capture'}
      sx={{ ...roundActionSx, bgcolor: hasComposerContent && !composerSaving ? colors.semantic.profileFlow.completionText : colors.semantic.line, color: hasComposerContent && !composerSaving ? colors.semantic.bg : colors.semantic.ink3 }}
    >
      {composerSaving ? <CircularProgress size={18} thickness={4} sx={{ color: 'inherit' }} /> : <SendIcon />}
    </IconButton>
  </Box>
);
